class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.renderStamp = 0;
    this.onCartLinkClick = this.onCartLinkClick.bind(this);
    this.onCartLinkKeydown = this.onCartLinkKeydown.bind(this);
    /* on the document: Escape closes the open drawer wherever focus is (it can sit on <body>) */
    this.onDocumentKeyup = (event) => {
      if (event.code === 'Escape' && this.classList.contains('active')) this.close();
    };

    /* delegated: #CartDrawer's markup (overlay included) is replaced by renderContents() */
    this.addEventListener('click', (event) => {
      if (event.target && event.target.id === 'CartDrawer-Overlay') this.close();
    });
  }

  connectedCallback() {
    /* delegated on the document: the header replaces #cart-icon-bubble when it re-renders in the theme editor */
    document.addEventListener('click', this.onCartLinkClick);
    document.addEventListener('keydown', this.onCartLinkKeydown);
    document.addEventListener('keyup', this.onDocumentKeyup);
    this.setHeaderCartIconAccessibility();

    this.sectionLoadHandler = () => this.setHeaderCartIconAccessibility();
    document.addEventListener('shopify:section:load', this.sectionLoadHandler);

    if (typeof subscribe === 'function') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'cart-items') return;
        /* add-to-cart flows call renderContents() right after publishing: only refresh when nobody did */
        const stamp = this.renderStamp;
        setTimeout(() => {
          if (stamp === this.renderStamp) this.refresh();
        });
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onCartLinkClick);
    document.removeEventListener('keydown', this.onCartLinkKeydown);
    document.removeEventListener('keyup', this.onDocumentKeyup);
    document.removeEventListener('shopify:section:load', this.sectionLoadHandler);
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    if (this.classList.contains('active')) document.body.classList.remove('overflow-hidden');
  }

  getCartLink(event) {
    return event.target instanceof Element ? event.target.closest('#cart-icon-bubble') : null;
  }

  onCartLinkClick(event) {
    const cartLink = this.getCartLink(event);
    if (!cartLink || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    this.open(cartLink);
  }

  onCartLinkKeydown(event) {
    if (!event.code || event.code.toUpperCase() !== 'SPACE') return;
    const cartLink = this.getCartLink(event);
    if (!cartLink) return;
    event.preventDefault();
    this.open(cartLink);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
  }

  get panel() {
    return this.querySelector('.drawer__inner');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    document.body.classList.add('overflow-hidden');

    /* already open (content was re-rendered while the drawer is showing): only re-arm the focus trap */
    if (this.classList.contains('active')) {
      this.trapPanelFocus();
      return;
    }

    clearTimeout(this.openTimer);
    if (this.openEndHandler) this.removeEventListener('transitionend', this.openEndHandler);

    const finish = () => {
      clearTimeout(this.openTimer);
      this.removeEventListener('transitionend', this.openEndHandler);
      this.openEndHandler = null;
      if (this.classList.contains('active')) this.trapPanelFocus();
    };

    /* focus moves in once the panel has finished sliding (also fires with reduced motion: .01ms, not none) */
    this.openEndHandler = (event) => {
      if (event.target === this.panel && event.propertyName === 'transform') finish();
    };
    this.addEventListener('transitionend', this.openEndHandler);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      const panel = this.panel;
      if (panel) panel.getBoundingClientRect(); // settle the closed position so the slide always runs
      this.classList.add('animate', 'active');
      /* safety net if no transitionend arrives (interrupted transition, background tab) */
      this.openTimer = setTimeout(finish, 800);
    });
  }

  trapPanelFocus(attempt = 0) {
    const panel = this.panel;
    if (!panel) return;
    clearTimeout(this.focusTimer);
    trapFocus(panel, panel);
    /* With reduced motion every element gets a .01ms transition, so the drawer's visibility reaches
       the panel a frame or two after transitionend and the first focus() can miss: try again briefly. */
    if (document.activeElement !== panel && attempt < 12 && this.classList.contains('active')) {
      this.focusTimer = setTimeout(() => this.trapPanelFocus(attempt + 1), 40);
    }
  }

  close() {
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    if (this.openEndHandler) {
      this.removeEventListener('transitionend', this.openEndHandler);
      this.openEndHandler = null;
    }
    this.classList.remove('active');
    /* the scroll lock goes first: UI that hides while the body is locked (the PDP sticky bar)
       must be visible again before its button can take focus back */
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.activeElement && this.activeElement.isConnected ? this.activeElement : null);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', cartDrawerNote.closest('details').hasAttribute('open'));

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* Called by every add-to-cart flow with the /cart/add.js response (sections: cart-drawer, cart-icon-bubble). */
  renderContents(parsedState) {
    this.renderStamp += 1;
    this.productId = parsedState ? parsedState.id : undefined;
    const sections = parsedState && parsedState.sections ? parsedState.sections : null;

    if (!sections || !sections['cart-drawer']) {
      /* the caller did not ask for the sections: fetch them */
      this.refresh().then(() => this.open());
      return;
    }

    this.renderSections(sections, '#CartDrawer');

    setTimeout(() => {
      this.open();
    });
  }

  /* drawerSelector: '#CartDrawer' swaps overlay + panel; '.fable-cart__content' keeps the panel
     element (and a running slide) and the status nodes beside the content */
  renderSections(sections, drawerSelector = '#CartDrawer') {
    this.getSectionsToRender().forEach((section) => {
      const selector = section.selector ? drawerSelector : undefined;
      const sectionElement = selector ? this.querySelector(selector) : document.getElementById(section.id);
      const html = sections[section.id];
      if (!sectionElement || !html) return;
      const newHTML = this.getSectionInnerHTML(html, selector);
      if (newHTML !== null) sectionElement.innerHTML = newHTML;
    });
    this.classList.toggle('is-empty', !this.querySelector('cart-drawer-items'));
    this.setHeaderCartIconAccessibility();
  }

  /* Re-reads the drawer and the header bubble from the server (cart changed by a script that did not render us).
     Asked for the current page, like the add / change calls (sections_url): on the cart URL the
     drawer snippet renders nothing, so the section would come back empty. */
  refresh() {
    const ids = this.getSectionsToRender().map((section) => section.id);
    return fetch(`${window.location.pathname}?sections=${ids.join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        if (!this.isConnected) return;
        this.renderSections(sections, '.fable-cart__content');
        if (this.classList.contains('active')) this.trapPanelFocus();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /* <cart-drawer> listens for foreign cart updates and re-renders the whole panel from section
     "cart-drawer"; the inherited handler would write cart-page markup into the drawer. */
  onCartUpdate() {}

  /* The live region sits in the panel beside .fable-cart__content, so it survives the swap and
     the new subtotal written into it is announced. */
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.fable-cart__content'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'CartDrawer-LiveRegionText',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
